import React, { useEffect, useMemo, useState } from "react";
import { Box, Divider, Grid, MenuItem, Alert } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzMuiAddButton from "src/components/core-components/mui/buttons/ByzMuiAddButton";
import ByzMuiDeleteButton from "src/components/core-components/mui/buttons/ByzMuiDeleteButton";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import { referenceService } from "src/api/references/referenceService";
import dayjs, { Dayjs } from "dayjs";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { WillOpenCardKey } from "src/api/request/requestService";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";

interface PathologyModalProps {
  open: boolean;
  onClose: () => void;
  serviceName?: string;
  onSave?: (requests: PathologyRequest[]) => void;
  // MCP IDs for dynamic lists
  sampleMethodMcpId?: number;
  locationMcpId?: number;
  baseFeatureMcpId?: number;
  usersMcpId?: number;
  sampleType?: string;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

interface PathologyRequest {
  id: string;
  sampleType: string;
  sampleTypeId: string; // API'den gelen SampleType field'ı için
  requestTime: Dayjs | null;
  sampleMethod: string;
  sampleTime: Dayjs | null;
  userId: string;
  location: string;
  baseFeature: string;
  tissue: string;
}

const emptyRequest = (sampleType: string): PathologyRequest => ({
  id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
  sampleType,
  sampleTypeId: "",
  requestTime: dayjs(),
  sampleMethod: "",
  sampleTime: dayjs(),
  userId: "",
  location: "",
  baseFeature: "",
  tissue: "",
});

const PathologyModal: React.FC<PathologyModalProps> = ({
  open,
  onClose,
  serviceName = "",
  onSave,
  sampleMethodMcpId = 0,
  locationMcpId = 0,
  baseFeatureMcpId = 0,
  usersMcpId = 0,
  sampleType = "",
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));

  // Numune türü, prop boş gelse bile ekranda direkt seçili görünsün
  // serviceName varsa onu, yoksa "Patoloji" sabitini kullan.
  const effectiveSampleType = sampleType || serviceName || t("patient-request.pathology.default-name");

  const [requests, setRequests] = useState<PathologyRequest[]>([
    emptyRequest(effectiveSampleType),
  ]);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const current = useMemo(
    () => requests.find((r) => r.id === selectedRequestId) || requests[0],
    [requests, selectedRequestId]
  );

  const [methodOptions, setMethodOptions] = useState<{ label: string; value: string }[]>([]);
  const [locationOptions, setLocationOptions] = useState<{ label: string; value: string }[]>([]);
  const [baseFeatureOptions, setBaseFeatureOptions] = useState<{ label: string; value: string }[]>(
    []
  );
  const [userOptions, setUserOptions] = useState<{ label: string; value: string }[]>([]);
  const [sampleTypeOptions, setSampleTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);
  // Field label'ları API'den almak için
  const [fieldLabels, setFieldLabels] = useState<Record<string, string>>({});
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRequestId, setDeleteRequestId] = useState<string | null>(null);

  // Seçili label'ları inputta göstermek için
  const selectedSampleMethodLabel = useMemo(() => {
    const found = methodOptions.find((opt) => opt.value === current.sampleMethod);
    return found?.label || "";
  }, [current.sampleMethod, methodOptions]);

  const selectedUserLabel = useMemo(() => {
    const found = userOptions.find((opt) => opt.value === current.userId);
    return found?.label || "";
  }, [current.userId, userOptions]);

  const selectedLocationLabel = useMemo(() => {
    const found = locationOptions.find((opt) => opt.value === current.location);
    return found?.label || "";
  }, [current.location, locationOptions]);

  const selectedBaseFeatureLabel = useMemo(() => {
    const found = baseFeatureOptions.find((opt) => opt.value === current.baseFeature);
    return found?.label || "";
  }, [current.baseFeature, baseFeatureOptions]);

  const selectedSampleTypeLabel = useMemo(() => {
    const found = sampleTypeOptions.find((opt) => opt.value === current.sampleTypeId);
    return found?.label || "";
  }, [current.sampleTypeId, sampleTypeOptions]);

  // reset on open
  useEffect(() => {
    if (open) {
      const first = emptyRequest(effectiveSampleType);
      setRequests([first]);
      setSelectedRequestId(first.id);
      setError("");
    }
  }, [open, effectiveSampleType]);

  // SampleType options yüklendiğinde ilk değeri otomatik seç
  useEffect(() => {
    if (sampleTypeOptions.length > 0 && !current.sampleTypeId) {
      updateCurrent({ sampleTypeId: sampleTypeOptions[0].value });
    }
  }, [sampleTypeOptions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // load options helper
  const loadMcpOptions = async (mcpId: number) => {
    if (!mcpId) return [];
    const res = await referenceService.mcParameterDetail.getBulkMcParameterDetails({
      mcpIds: [mcpId],
    });
    const list =
      res?.data?.[0]?.mcparameterdetails ||
      res?.data?.find?.((d: any) => d?.mcp_id === mcpId)?.mcparameterdetails ||
      [];
    if (!Array.isArray(list)) return [];
    return list.map((d: any) => ({
      label: d?.name || d?.Name || d?.NAME || d?.description || "",
      value: String(d?.id ?? d?.ID ?? d?.mcpd_id ?? d?.MCPD_ID ?? d?.code ?? ""),
    }));
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [methods, locations, baseFeatures] = await Promise.all([
          loadMcpOptions(sampleMethodMcpId),
          loadMcpOptions(locationMcpId),
          loadMcpOptions(baseFeatureMcpId),
        ]);
        setMethodOptions(methods);
        setLocationOptions(locations);
        setBaseFeatureOptions(baseFeatures);

        // Kullanıcı listesi için sadece MCP / AdditionalInfo kaynaklarını kullan
        if (usersMcpId) {
          const users = await loadMcpOptions(usersMcpId);
          setUserOptions(users);
        }
      } catch (e) {
        console.error("Pathology options load error", e);
        setError("Liste verileri yüklenemedi.");
      } finally {
        setLoading(false);
      }
    };
    if (open) fetchAll();
  }, [open, sampleMethodMcpId, locationMcpId, baseFeatureMcpId, usersMcpId, selectedOfficeId]);

  // PATHOLOGY form tanımını AdditionalInfo/fields üzerinden yükle
  useEffect(() => {
    if (!open) return;

    const loadAdditionalInfo = async () => {
      try {
        const res = await referenceService.additionalInfo.getFields({
          formtype: "PATHOLOGY",
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
          // Hangi hizmet için PATHOLOGY formu çalışıyorsa o hizmetin Id'si
          serviceid: additionalInfoParams?.serviceId
            ? Number(additionalInfoParams.serviceId)
            : undefined,
        });
        setAdditionalInfo(res);

        const fields = res.fields || [];

        // Field label'larını sakla
        const labels: Record<string, string> = {};
        fields.forEach((f: any) => {
          if (f.fieldname && f.fieldlabel) {
            labels[f.fieldname] = f.fieldlabel;
          }
        });
        setFieldLabels(labels);

        const userField = fields.find((f) => f.fieldname === "PUserName");
        if (userField && Array.isArray(userField.options)) {
          setUserOptions(
            userField.options.map((opt) => ({
              label: opt.label ?? "",
              value: String(opt.value),
            }))
          );
        }

        const tissueField = fields.find((f) => f.fieldname === "TissueSamplesId");
        if (tissueField && Array.isArray(tissueField.options)) {
          setBaseFeatureOptions(
            tissueField.options.map((opt) => ({
              label: opt.label ?? "",
              value: String(opt.value),
            }))
          );
        }

        const samplingField = fields.find((f) => f.fieldname === "SamplingMethodId");
        if (samplingField && Array.isArray(samplingField.options)) {
          setMethodOptions(
            samplingField.options.map((opt) => ({
              label: opt.label ?? "",
              value: String(opt.value),
            }))
          );
        }

        const locationField = fields.find((f) => f.fieldname === "LocationId");
        if (locationField && Array.isArray(locationField.options)) {
          setLocationOptions(
            locationField.options.map((opt) => ({
              label: opt.label ?? "",
              value: String(opt.value),
            }))
          );
        }

        const sampleTypeField = fields.find((f) => f.fieldname === "SampleType");
        if (sampleTypeField && Array.isArray(sampleTypeField.options)) {
          const options = sampleTypeField.options.map((opt) => ({
            label: opt.label ?? "",
            value: String(opt.value),
          }));
          setSampleTypeOptions(options);
          
          // Eğer sadece bir option varsa veya default değer varsa otomatik seç
          if (options.length === 1 && !current.sampleTypeId) {
            updateCurrent({ sampleTypeId: options[0].value });
          }
        }
      } catch {
        // Hata durumunda mevcut MCP / doktor fallback akışı devam etsin.
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoParams, selectedOfficeId]);

  const updateCurrent = (patch: Partial<PathologyRequest>) => {
    setRequests((prev) =>
      prev.map((r) => (r.id === current.id ? { ...r, ...patch } : r))
    );
  };

  const handleSaveRequest = () => {
    const reqs = requests;
    const allValid = reqs.every(
      (r) =>
        r.sampleType &&
        r.requestTime &&
        r.sampleMethod &&
        r.sampleTime &&
        r.userId &&
        r.location &&
        r.baseFeature
    );
    if (!allValid) {
      setError(t("patient-request.pathology.errors.required"));
      return;
    }
    setError("");
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setError("");
      onSave?.(reqs);
      if (!onSave) {
        onClose();
      }
    }, 0);
  };

  const handleAddRequest = () => {
    const newReq = emptyRequest(effectiveSampleType);
    setRequests((prev) => [...prev, newReq]);
    setSelectedRequestId(newReq.id);
  };

  const handleDeleteRequest = (id: string) => {
    if (requests.length === 1) return;
    setDeleteRequestId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteRequest = async (): Promise<boolean> => {
    if (!deleteRequestId) return false;
    setRequests((prev) => prev.filter((r) => r.id !== deleteRequestId));
    setSelectedRequestId((prevId) => {
      if (prevId === deleteRequestId) {
        const remaining = requests.filter((r) => r.id !== deleteRequestId);
        return remaining.length ? remaining[0].id : null;
      }
      return prevId;
    });
    setDeleteRequestId(null);
    setDeleteDialogOpen(false);
    return true;
  };

  const isReady = requests.every(
    (r) =>
      r.sampleType &&
      r.requestTime &&
      r.sampleMethod &&
      r.sampleTime &&
      r.userId &&
      r.location &&
      r.baseFeature
  );

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ByzMuiTypography variant="h6" fontWeight={700} sx={{ color: "#111827" }}>
            {t("patient-request.pathology.title")}
          </ByzMuiTypography>
          <ByzMuiTypography variant="body2" sx={{ color: "text.secondary" }}>
            {serviceName}
          </ByzMuiTypography>
        </Box>
      </Box>

      <Box sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 1.5 } }}>
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSaveRequest} disabled={!isReady || saving} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            mx: -2,
            mb: 2,
            py: 1.5,
            px: 2,
            backgroundColor: "#F4F7FE",
            borderTop: "1px solid #E0E0E0",
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          <ByzMuiTypography variant="subtitle1" fontWeight={600}>
            {t("patient-request.pathology.subtitle")}
          </ByzMuiTypography>
        </Box>

        <Grid container spacing={2} alignItems="center">
          {sampleTypeOptions.length > 0 && (
            <>
              <Grid item xs={12} sm={4}>
                <ByzMuiTypography variant="subtitle2" fontWeight={600}>
                  {fieldLabels.SampleType || "Numune Türü"}
                </ByzMuiTypography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <ByzMuiTextField
                  fullWidth
                  size="small"
                  value={selectedSampleTypeLabel || ""}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f5f5",
                      "& fieldset": {
                        borderColor: "#2498f3",
                      },
                    },
                  }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={4}>
            <ByzMuiTypography variant="subtitle2" fontWeight={600}>
              {t("patient-request.pathology.request-time")}
            </ByzMuiTypography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzMuiDateTimePicker
              sx={{ width: "100%" }}
              value={current.requestTime}
              onChange={(dt) => updateCurrent({ requestTime: dt })}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  label: t("patient-request.pathology.request-time"),
                  // Her zaman mavi görünmesi için zorunlu/otomatik hata durumlarını kapat
                  required: false,
                  error: false,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <ByzMuiTypography variant="subtitle2" fontWeight={600}>
              {fieldLabels.SamplingMethodId || t("patient-request.pathology.sample-method")}
            </ByzMuiTypography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.pathology.select")}
              value={selectedSampleMethodLabel}
              selectedId={current.sampleMethod}
              dialogMaxHeight="60vh"
              staticData={methodOptions.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              disabled={loading || methodOptions.length === 0}
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  updateCurrent({ sampleMethod: String(first.id) });
                } else {
                  updateCurrent({ sampleMethod: "" });
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <ByzMuiTypography variant="subtitle2" fontWeight={600}>
              {t("patient-request.pathology.sample-time")}
            </ByzMuiTypography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzMuiDateTimePicker
              sx={{ width: "100%" }}
              value={current.sampleTime}
              onChange={(dt) => updateCurrent({ sampleTime: dt })}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  label: t("patient-request.pathology.sample-time"),
                  required: false,
                  error: false,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <ByzMuiTypography variant="subtitle2" fontWeight={600}>
              {t("patient-request.pathology.user")}
            </ByzMuiTypography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.pathology.select")}
              value={selectedUserLabel}
              selectedId={current.userId}
              dialogMaxHeight="60vh"
              staticData={userOptions.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              disabled={loading || userOptions.length === 0}
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  updateCurrent({ userId: String(first.id) });
                } else {
                  updateCurrent({ userId: "" });
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <ByzMuiTypography variant="subtitle2" fontWeight={600}>
              {t("patient-request.pathology.location")}
            </ByzMuiTypography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.pathology.select")}
              value={selectedLocationLabel}
              selectedId={current.location}
              dialogMaxHeight="60vh"
              staticData={locationOptions.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              disabled={loading || locationOptions.length === 0}
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  updateCurrent({ location: String(first.id) });
                } else {
                  updateCurrent({ location: "" });
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <ByzMuiTypography variant="subtitle2" fontWeight={600}>
              {t("patient-request.pathology.base-feature")}
            </ByzMuiTypography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.pathology.select")}
              value={selectedBaseFeatureLabel}
              selectedId={current.baseFeature}
              dialogMaxHeight="60vh"
              staticData={baseFeatureOptions.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              disabled={loading || baseFeatureOptions.length === 0}
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  updateCurrent({ baseFeature: String(first.id) });
                } else {
                  updateCurrent({ baseFeature: "" });
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <ByzMuiTypography variant="subtitle2" fontWeight={600}>
              {t("patient-request.pathology.tissue")}
            </ByzMuiTypography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzMuiTextField
              fullWidth
              multiline
              minRows={1}
              value={current.tissue}
              onChange={(e) => updateCurrent({ tissue: e.target.value })}
              placeholder={""}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mx: -2,
            my: 1,
            py: 1.5,
            px: 2,
            backgroundColor: "#F4F7FE",
            borderTop: "1px solid #E0E0E0",
            borderBottom: "1px solid #E0E0E0",
          }}
        >
          <ByzMuiTypography variant="subtitle1" fontWeight={600}>
            {t("patient-request.pathology.requests")}
          </ByzMuiTypography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
          <ByzMuiAddButton onClick={handleAddRequest} disabled={saving}>
            {t("patient-request.pathology.add")}
          </ByzMuiAddButton>
          <ByzMuiDeleteButton
            onClick={() => current && handleDeleteRequest(current.id)}
            disabled={saving || requests.length <= 1}
          >
            {t("patient-request.pathology.delete")}
          </ByzMuiDeleteButton>
          <ByzMuiTypography variant="body2" color="text.secondary">
            {t("patient-request.pathology.total", { count: requests.length } as any)}
          </ByzMuiTypography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {requests.map((r) => (
            <ByzMuiCheckBox
              key={r.id}
              text={`${serviceName || t("patient-request.pathology.selected-service")} (${r.sampleType || t("patient-request.pathology.sample")})`}
              checked={current?.id === r.id}
              onChange={() => setSelectedRequestId(r.id)}
            />
          ))}
        </Box>
      </Box>

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteRequestId(null);
        }}
        onConfirm={confirmDeleteRequest}
        title={t("delete-confirm.title")}
        content={t("delete-confirm.content")}
      />
    </OpenComponentDialog>
  );
};

export default PathologyModal;
